import { Box, Flex, IconButton } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Condition } from 'Component/others';
import { TextBold, TextRegular } from 'UiKit/typography';
import { CloseIcon, LocationIcon, MicrophoneIcon, StoreIcon, PencilOutlineIcon, TrashOutlineIcon, UserIcon } from 'Asset/icon';
import { getMenuPermissions } from 'Helper/getMenuPermissions';

import formatArrayWithPipe from 'View/schedule/helper/formatArrayWithPipe';
import useModalDeleteSchedule from 'View/schedule/helper/useModalDeleteSchedule';
import { useScheduleState } from 'View/schedule/state';

import styles from './styles.module.css';

const OverlayCard = ({ isOpen, onClose, eventInfo, clickPosition }) => {
  const { t } = useTranslation('schedule');
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const schedulePermission = getMenuPermissions('schedule', 'schedule');
  const { updateState } = useScheduleState();
  const { onOpenModalDeleteSchedule } = useModalDeleteSchedule();

  useEffect(() => {
    if (isOpen && clickPosition) {
      setShouldRender(true);

      // Calculate card position
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const CARD_WIDTH = 320;
      const CARD_HEIGHT = 300;
      const SPACING = 8;

      // right of the clicked event
      let left = clickPosition.x + clickPosition.width + SPACING;
      let top = clickPosition.y;

      // left card position handle
      if (left + CARD_WIDTH > viewportWidth) left = clickPosition.x - CARD_WIDTH - SPACING;

      // top card position handle
      if (top + CARD_HEIGHT > viewportHeight) top = viewportHeight - CARD_HEIGHT - SPACING;
      if (top < SPACING) top = SPACING;

      setPosition({ top, left });
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);

      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, clickPosition]);

  if (!shouldRender) return null;

  let { eventName, accounts, hosts, studio, operators } = eventInfo?.event?.extendedProps || {};
  const startDate = eventInfo?.event?.start;
  const timeText = eventInfo?.timeText || '';

  const handleEditEvent = () => {
    if (eventInfo) {
      const { event } = eventInfo;
      updateState({
        isOpenFormUpsert: true,
        isEditFormUpsert: true,
        initialSelectedSchedule: {
          ...event.extendedProps,
          id: event.id,
          selectedDate: event.start,
          startedAt: event.start,
          endedAt: event.end,
        },
      });
      onClose();
    }
  };

  const handleDeleteEvent = () => {
    if (eventInfo) {
      onOpenModalDeleteSchedule(eventInfo.event.id);
      onClose();
    }
  };

  return (
    <OutsideClickHandler onOutsideClick={onClose}>
      <Box
        className={`${styles['overlay__card']} ${isVisible ? styles['overlay__card--visible'] : ''}`}
        style={{ position: 'fixed', top: `${position.top}px`, left: `${position.left}px` }}
      >
        <Flex className={styles['head__section']}>
          <Box className={styles['status-indicator']} bg={'var(--color-success60)'} />
          <TextBold className={styles['overlay__event-name']}>{eventName}</TextBold>
        </Flex>

        <Flex gap={1} className={styles['action-buttons']}>
          <Condition statement={schedulePermission?.isCanEdit}>
            <IconButton
              icon={<PencilOutlineIcon />}
              onClick={handleEditEvent}
              aria-label={t('ariaEditEvent')}
              variant="ghost"
              size="sm"
              className={styles['action-button']}
            />
          </Condition>

          <Condition statement={schedulePermission?.isCanDelete}>
            <IconButton
              icon={<TrashOutlineIcon />}
              onClick={handleDeleteEvent}
              aria-label={t('ariaDeleteEvent')}
              variant="ghost"
              size="sm"
              className={styles['action-button']}
            />
          </Condition>
          <IconButton icon={<CloseIcon />} onClick={onClose} aria-label={t('ariaCloseOverlay')} variant="ghost" size="sm" className={styles['action-button']} />
        </Flex>

        <Flex className={styles['date__section']}>
          <TextRegular>
            {moment(startDate).format('ddd DD/MM/YYYY')} | {timeText}
          </TextRegular>
        </Flex>

        <Box className={styles['overlay__content']}>
          <Condition statement={accounts && accounts.length > 0}>
            <Flex className={styles['info-row']}>
              <StoreIcon color="var(--color-neutral70)" />
              <TextRegular>{formatArrayWithPipe(accounts)}</TextRegular>
            </Flex>
          </Condition>

          <Condition statement={studio}>
            <Flex className={styles['info-row']}>
              <LocationIcon color="var(--color-neutral70)" />
              <Box>
                <TextBold>{studio?.name}</TextBold>
                <TextRegular>{studio?.room}</TextRegular>
              </Box>
            </Flex>
          </Condition>

          <Condition statement={hosts && hosts.length > 0}>
            <Flex className={styles['info-row']}>
              <MicrophoneIcon color="var(--color-neutral70)" />
              <TextRegular>{formatArrayWithPipe(hosts)}</TextRegular>
            </Flex>
          </Condition>

          <Condition statement={operators && operators.length > 0}>
            <Flex className={styles['info-row']}>
              <UserIcon color="var(--color-neutral70)" />
              <TextRegular>{formatArrayWithPipe(operators)}</TextRegular>
            </Flex>
          </Condition>
        </Box>
      </Box>
    </OutsideClickHandler>
  );
};

export default OverlayCard;
